import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import React, { useEffect, useState } from "react";
import {
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    serverTimestamp,
    where,
    getDocs,
    doc,
    updateDoc,
    increment,
} from "firebase/firestore";
import {
    MainContainer,
    ChatContainer,
    MessageList,
    Message,
    MessageInput,
    Avatar,
} from "@chatscope/chat-ui-kit-react";
import moment from "moment";
import DOMPurify from "dompurify";
import { useFirebase } from "contexts/FirebaseAuthContext";
import { sanitizeURLs } from "utils/helpers";
import userDummyImg from "assets/images/users/user-dummy-img.jpg";
import { Message as MessageInterface } from 'types/campaign/collaborations/chat';
import { EntityChatRoom } from 'types/chat';

export const CHAT_ALLOWED_TAGS = [
    "b", "i", "u", "strong", "em", "br",
    "ul", "ol", "li",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "p", "a",
];

export const CHAT_ALLOWED_ATTR = ["href", "rel", "target"];

export const getMessageGroupKey = (
    msg: { senderId: string; senderEntityId?: string },
    currentEntityId: string | null | undefined,
    influencerUuid: string | undefined
): string => {
    if (influencerUuid && msg.senderEntityId === influencerUuid) {
        return `incoming-influencer:${msg.senderId}`;
    }
    if (currentEntityId && msg.senderEntityId === currentEntityId) {
        return `outgoing-hub:${msg.senderId}`;
    }
    return `incoming:${msg.senderId}`;
};

const getGroupKey = (msg: { __groupKey?: string; senderId: string }) =>
    msg.__groupKey ?? msg.senderId;

const getMessageDayKey = (msg: { timestamp?: unknown }) =>
    moment(parseFirestoreTimestamp(msg.timestamp) ?? new Date()).startOf("day").toISOString();

export const getMessagePosition = (currentMsg: any, lastMsg: any, nextMsg: any) => {
    const currentKey = getGroupKey(currentMsg);
    const lastKey = lastMsg ? getGroupKey(lastMsg) : null;
    const nextKey = nextMsg ? getGroupKey(nextMsg) : null;

    if ((!lastKey || lastKey !== currentKey) && (!nextKey || nextKey !== currentKey)) {
        return "single";
    }
    if ((!lastKey || lastKey !== currentKey) && nextKey === currentKey) {
        return "first";
    }
    if (lastKey === currentKey && (!nextKey || nextKey !== currentKey)) {
        return "last";
    }
    if (lastKey === currentKey && nextKey === currentKey) {
        return "normal";
    }
    return "single";
};

export const groupMessagesByDate = (messages: MessageInterface[]) => {
    const groups: Record<string, MessageInterface[]> = {};
    messages.forEach((msg) => {
        const dateKey = msg.props.model.date;
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(msg);
    });
    return groups;
};

export const parseFirestoreTimestamp = (timestamp: unknown): Date | null => {
    if (timestamp == null) return null;

    try {
        if (typeof (timestamp as { toDate?: () => Date }).toDate === "function") {
            const date = (timestamp as { toDate: () => Date }).toDate();
            return moment(date).isValid() ? date : null;
        }

        if (typeof (timestamp as { seconds?: number }).seconds === "number") {
            const date = new Date((timestamp as { seconds: number }).seconds * 1000);
            return moment(date).isValid() ? date : null;
        }

        if (timestamp instanceof Date) {
            return moment(timestamp).isValid() ? timestamp : null;
        }

        const parsed = moment(timestamp as string | number | Date);
        return parsed.isValid() ? parsed.toDate() : null;
    } catch {
        return null;
    }
};

export const getDayLabel = (dateIso: string) => {
    const m = moment(dateIso);
    if (!dateIso || !m.isValid()) return "Today";
    if (m.isSame(moment(), "day")) return "Today";
    if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
    if (m.isAfter(moment().subtract(6, "days"), "day")) return m.format("dddd");
    return m.format("DD/MM/YYYY");
};

export interface UseCollaborationChatMessagesParams {
    entityChatRoom: EntityChatRoom | null;
    currentEntityId: string | null | undefined;
    setEntityChatRoom?: React.Dispatch<React.SetStateAction<EntityChatRoom | null>>;
}

export function useCollaborationChatMessages({
    entityChatRoom,
    currentEntityId,
    setEntityChatRoom,
}: UseCollaborationChatMessagesParams) {
    const { firebaseUser, db, auth, appUser } = useFirebase();

    const [input, setInput] = useState("");
    const [participants, setParticipants] = useState<Record<string, any>>({});
    const [participantsIDs, setParticipantsIDs] = useState<string[]>([]);
    const [rawMessages, setRawMessages] = useState<any[]>([]);
    const [groupedMessages, setGroupedMessages] = useState<Record<string, MessageInterface[]>>({});


    useEffect(() => {
        if (!entityChatRoom || !firebaseUser || !entityChatRoom.uuid) return;
        const roomId = entityChatRoom.uuid;

        const q = query(
            collection(db, "chatRooms", roomId, "messages"),
            orderBy("timestamp", "asc")
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const newMessages = snapshot.docs
                    .map((docSnap) => ({
                        id: docSnap.id,
                        ...docSnap.data(),
                    }))
                    .filter((m: any) => m.senderId);

                const senderIdsArray = newMessages.map((m: any) => m.senderId);
                const uniqueSenderIds: string[] = [];
                for (let i = 0; i < senderIdsArray.length; i++) {
                    if (uniqueSenderIds.indexOf(senderIdsArray[i]) === -1) {
                        uniqueSenderIds.push(senderIdsArray[i]);
                    }
                }
                setParticipantsIDs(uniqueSenderIds);
                setRawMessages(newMessages);
            },
            (error) => {
                console.error("Error subscribing to chat messages:", error);
            }
        );

        return () => unsubscribe();
    }, [entityChatRoom, firebaseUser, db]);

    useEffect(() => {
        if (participantsIDs.length === 0) return;

        const fetchParticipants = async () => {
            const usersRef = collection(db, "users");
            const idsToFetch = participantsIDs.filter((id) => !participants[id]);
            if (idsToFetch.length === 0) return;

            const batches = [];
            for (let i = 0; i < idsToFetch.length; i += 10) {
                batches.push(idsToFetch.slice(i, i + 10));
            }

            const newParticipants: Record<string, any> = {};
            for (const batch of batches) {
                const q = query(usersRef, where("__name__", "in", batch));
                const snapshot = await getDocs(q);
                snapshot.forEach((docSnap) => {
                    newParticipants[docSnap.id] = docSnap.data();
                });
            }

            if (Object.keys(newParticipants).length > 0) {
                setParticipants((prev) => ({ ...prev, ...newParticipants }));
            }
        };

        fetchParticipants();
    }, [participants, participantsIDs, db]);

    const transformMessages = (messages: any[]): Array<MessageInterface> => {
        const influencerUuid = entityChatRoom?.influencer?.uuid;
        const messagesWithKeys = messages.map((msg) => ({
            ...msg,
            __groupKey: getMessageGroupKey(msg, currentEntityId, influencerUuid),
        }));

        return messagesWithKeys.map((msg, i) => {
            const lastMsg = messagesWithKeys[i - 1];
            const nextMsg = messagesWithKeys[i + 1];
            const currentDay = getMessageDayKey(msg);
            const lastMsgForPosition =
                lastMsg && getMessageDayKey(lastMsg) === currentDay ? lastMsg : undefined;
            const nextMsgForPosition =
                nextMsg && getMessageDayKey(nextMsg) === currentDay ? nextMsg : undefined;
            const position = getMessagePosition(msg, lastMsgForPosition, nextMsgForPosition);
            const isInfluencerMessage = Boolean(
                influencerUuid && msg.senderEntityId === influencerUuid
            );
            const direction =
                msg.senderEntityId === currentEntityId ? "outgoing" : "incoming";
            const sender = participants[msg.senderId];
            const messageDate = parseFirestoreTimestamp(msg.timestamp) ?? new Date();
            const isGroupStart = ["single", "first"].includes(position);
            const senderDisplayNameBase = isInfluencerMessage // Base name before hub [Manager] suffix
                ? entityChatRoom?.influencer?.name ?? sender?.name ?? msg.senderId
                : msg.senderId === auth.currentUser?.uid
                    ? appUser?.name ?? sender?.name ?? msg.senderId
                    : sender?.name ?? msg.senderId;
            const isHubParticipant =
                sender?.access_type === "hub" || // Hub sender from loaded participant access_type
                (msg.senderId === auth.currentUser?.uid && appUser?.access_type === "hub"); // Own hub user before participant fetch
            const senderDisplayName =
                isHubParticipant && !isInfluencerMessage // Append [Manager] for hub participants in chat UI
                    ? `${senderDisplayNameBase} [Manager]`
                    : senderDisplayNameBase;

            const children: React.ReactElement[] = [];

            if (direction === "incoming" && isGroupStart) {
                const avatarSrc = isInfluencerMessage
                    ? entityChatRoom?.influencer?.profile_picture_url ??
                      sender?.profile_photo_url
                    : sender?.profile_photo_url;
                children.push(
                    <Avatar
                        key="avatar"
                        name={senderDisplayName}
                        style={{ height: 30, width: 30, minHeight: "30px", minWidth: "30px" }}
                        src={avatarSrc ?? userDummyImg}
                    />
                );
            }

            const isOwnMessage = msg.senderId === auth.currentUser?.uid;

            if (isGroupStart && senderDisplayName && !isOwnMessage) {
                const influencerAvatarSrc =
                    entityChatRoom?.influencer?.profile_picture_url ??
                    sender?.profile_photo_url;

                children.push(
                    <Message.Header
                        key="header"
                        className={`chat-sender-group-name${isInfluencerMessage ? " chat-sender-group-name--influencer" : ""}`}
                    >
                        {isInfluencerMessage ? (
                            <span className="chat-sender-group-name__label">
                                <span>{senderDisplayName}</span>
                            </span>
                        ) : (
                            senderDisplayName
                        )}
                    </Message.Header>
                );
            }

            return {
                type: "message",
                props: {
                    model: {
                        message: msg.text ?? "",
                        sentTime: moment(messageDate).format("h:mma"),
                        sender: senderDisplayName,
                        direction,
                        position,
                        senderId: msg.senderId,
                        date: moment(messageDate).startOf("day").toISOString(),
                        isOwnMessage,
                    },
                    ...(children.length > 0 ? { children } : {}),
                    ...((direction === "incoming" && ["normal", "last"].includes(position))
                        ? { avatarSpacer: true }
                        : {}),
                },
            };
        });
    };

    useEffect(() => {
        const transformed = transformMessages(rawMessages);
        setGroupedMessages(groupMessagesByDate(transformed));
    }, [
        participants,
        rawMessages,
        currentEntityId,
        auth.currentUser?.uid,
        appUser?.name,
        entityChatRoom?.influencer?.uuid,
        entityChatRoom?.influencer?.name,
    ]);

    const sendMessage = async () => {
        if (
            !input.trim() ||
            !auth.currentUser ||
            !entityChatRoom?.uuid ||
            !appUser ||
            entityChatRoom.access !== "w"
        ) {
            return;
        }

        const timestamp = serverTimestamp();
        const roomId = entityChatRoom.uuid;
        const message = {
            senderId: auth.currentUser.uid,
            text: input,
            timestamp,
            senderEntityId: currentEntityId,
        };
        await addDoc(collection(db, "chatRooms", roomId, "messages"), message);

        const lastMessage = {
            ...message,
            senderName:
                appUser.access_type === "hub" // Hub sender lastMessage preview includes [Manager]
                    ? `${appUser.name} [Manager]`
                    : appUser.name,
            senderProfileUrl: appUser.profile_photo_url,
        };

        const chatRoomRef = doc(db, "chatRooms", roomId);
        updateDoc(chatRoomRef, { lastMessage });

        if (Array.isArray(entityChatRoom.entities)) {
            for (const entityId of entityChatRoom.entities) {
                const entityChatRef = doc(db, "entityChatRooms", entityId, "rooms", roomId);
                updateDoc(entityChatRef, {
                    lastMessage,
                    ...(entityId === currentEntityId
                        ? { unreadCount: 0 }
                        : { unreadCount: increment(1) }),
                });
            }
        }

        setInput("");
    };

    const markAsRead = async (): Promise<boolean> => {
        if (!entityChatRoom || !currentEntityId) return false;
        if ((entityChatRoom.unreadCount ?? 0) <= 0) return false;
        try {
            const roomId = entityChatRoom.uuid;
            if (!roomId) return false;
            const entityChatRef = doc(db, "entityChatRooms", currentEntityId, "rooms", roomId);
            await updateDoc(entityChatRef, { unreadCount: 0 });
            setEntityChatRoom?.((prev) => (prev ? { ...prev, unreadCount: 0 } : null));
            return true;
        } catch (error) {
            console.error("Failed to mark as read:", error);
            return false;
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();

        const html = e.clipboardData.getData("text/html");
        const text = e.clipboardData.getData("text/plain");

        const cleanHTML = html
            ? DOMPurify.sanitize(html, { ALLOWED_TAGS: CHAT_ALLOWED_TAGS, ALLOWED_ATTR: CHAT_ALLOWED_ATTR })
            : text.replace(/\n/g, "<br>");

        setInput((prev) => prev + cleanHTML);
    };

    return {
        input,
        setInput,
        groupedMessages,
        sendMessage,
        markAsRead,
        handlePaste,
    };
}

export type CollaborationChatMessagesState = ReturnType<typeof useCollaborationChatMessages>;

export interface CollaborationChatMessagesProps {
    entityChatRoom: EntityChatRoom | null;
    messagesState: CollaborationChatMessagesState;
    loading?: boolean;
    showEmptyState?: boolean;
    dayLabelVariant?: "class" | "inline";
    messageInputClassName?: string;
}

const CollaborationChatMessages: React.FC<CollaborationChatMessagesProps> = ({
    entityChatRoom,
    messagesState,
    loading = false,
    showEmptyState = false,
    dayLabelVariant = "class",
    messageInputClassName,
}) => {
    const { input, setInput, groupedMessages, sendMessage, handlePaste, markAsRead } = messagesState;
    const [unreadMsgsBar, setUnreadMsgsBar] = useState(true);
    const unreadCount = entityChatRoom?.unreadCount ?? 0;
    const hasUnread = unreadCount > 0;
    const dayDividerTop = hasUnread && unreadMsgsBar ? "2.3rem" : "1rem";

    if (!entityChatRoom) {
        return null;
    }

    return (
        <div className="collaboration-chat-messages position-relative h-100">
        {hasUnread && (
            <div className="position-absolute z-2 d-flex w-100 align-items-center justify-content-center flex-column overflow-hidden">
                <div
                    className={`unread-msg-bar ${!unreadMsgsBar ? "hide" : ""} d-flex w-100 align-items-center justify-content-center flex-column`}
                >
                    <div
                        className="confirmation-bar unread-msg-bar-bg w-100 d-flex justify-content-between align-items-center h-max-content px-3 h-auto py-1"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <span>
                            You have {unreadCount} unread {unreadCount > 1 ? "messages" : "message"}.
                        </span>
                        <div className="d-flex justify-content-end align-items-center gap-2">
                            |{" "}
                            <button type="button" className="unset-all" onClick={() => markAsRead()}>
                                Mark as Read
                            </button>
                        </div>
                    </div>
                    <div
                        className="unread-msg-bar-toggle cursor-pointer"
                        onClick={() => setUnreadMsgsBar(!unreadMsgsBar)}
                    >
                        <span>
                            {unreadMsgsBar ? (
                                <i className="ri-arrow-up-s-line" />
                            ) : (
                                <i className="ri-arrow-down-s-line" />
                            )}
                        </span>
                    </div>
                </div>
            </div>
        )}
        <MainContainer>
            <ChatContainer>
                <MessageList className="mb-2">
                    {showEmptyState && Object.keys(groupedMessages).length === 0 && !loading && (
                        <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                            <div className="length-zero m-0">
                                <h3>Send a message to start the conversation</h3>
                            </div>
                        </div>
                    )}
                    {Object.keys(groupedMessages).flatMap((dateKey) => [
                        <div
                            key={`day-${dateKey}`}
                            className={
                                dayLabelVariant === "class"
                                    ? "position-sticky text-center my-2 d-flex align-items-center justify-content-center chat-day-divider"
                                    : "position-sticky text-center my-2 d-flex align-items-center justify-content-center"
                            }
                            style={{ zIndex: 10, top: dayDividerTop }}
                        >
                            {dayLabelVariant === "class" ? (
                                <div className="chat-day-label">{getDayLabel(dateKey)}</div>
                            ) : (
                                <div
                                    style={{
                                        zIndex: 10,
                                        background: "#f8f9fa",
                                        borderRadius: "8px",
                                        display: "inline-block",
                                        padding: "3px 10px",
                                        fontSize: "12px",
                                        color: "#555",
                                    }}
                                >
                                    {getDayLabel(dateKey)}
                                </div>
                            )}
                        </div>,
                        ...groupedMessages[dateKey].map((msg, i) => {
                            const isOwnMessage = Boolean(msg.props.model.isOwnMessage);
                            const useIncomingBubbleStyle =
                                msg.props.model.direction === "incoming" ||
                                (msg.props.model.direction === "outgoing" && !isOwnMessage);
                            const contentStyleClass = useIncomingBubbleStyle ? "incoming" : "outgoing";

                            return (
                                <Message
                                    key={`${dateKey}-${i}`}
                                    {...msg.props}
                                    className={
                                        msg.props.model.direction === "outgoing" && !isOwnMessage
                                            ? "chat-message-other-outgoing"
                                            : undefined
                                    }
                                >
                                    {msg.props.children}
                                    <Message.CustomContent>
                                        <Message.HtmlContent
                                            className={`message-html-content ${contentStyleClass}`}
                                            html={DOMPurify.sanitize(
                                                sanitizeURLs(msg.props?.model.message ?? ""),
                                                {
                                                    ALLOWED_TAGS: CHAT_ALLOWED_TAGS,
                                                    ALLOWED_ATTR: CHAT_ALLOWED_ATTR,
                                                }
                                            )}
                                        />
                                        <span className={`msg-sent-time ${contentStyleClass}`}>
                                            {msg.props.model.sentTime}
                                        </span>
                                    </Message.CustomContent>
                                </Message>
                            );
                        }),
                    ])}
                </MessageList>

                <MessageInput
                    placeholder={
                        entityChatRoom.access !== "w"
                            ? "You cannot send messages in this chat"
                            : "Type your message..."
                    }
                    value={input}
                    className={messageInputClassName}
                    onChange={(val) => setInput(val)}
                    onSend={sendMessage}
                    attachButton={false}
                    sendDisabled={entityChatRoom.access !== "w"}
                    disabled={entityChatRoom.access !== "w"}
                    onPaste={handlePaste}
                />
            </ChatContainer>
        </MainContainer>
        </div>
    );
};

export default CollaborationChatMessages;
